import { Box, Flex, Skeleton, Text } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { TenantRole } from "@nens/rana/api-v1";
import { User } from "@nens/rana/api-v1";
import { RanaForm } from "@nens/ranalib";
import { capitalizeFirst } from "@nens/shared-utils";

import { useShareKeyQuery } from "@/queries/query-share-key";
import { useUserQuery } from "@/queries/query-user";
import { userRoleDescriptionString, userRoleString } from "@/translations";
import { getCompleteName } from "@/utils/get-complete-name";

import { AnyUser } from "../types/any-user";

type ApiKeyUser = User & { apiKey: true };

export function ProfileModalForm({ user }: { user: User }) {
  if ("apiKey" in user && user.apiKey) {
    return <ProfileModalFormApiKeyUser user={user as ApiKeyUser} />;
  }

  return <ProfileModalFormRegularUser user={user} />;
}

function ProfileModalFormApiKeyUser({ user }: { user: ApiKeyUser }) {
  const intl = useIntl();

  const { data: apiKeyRead, isLoading, isSuccess } = useShareKeyQuery(user.id);

  const noPermission = intl.formatMessage({
    id: "profile.no_permission",
    defaultMessage: "No permission",
  });

  let createdBy: string | null = null;
  let email: string | null = null;
  if (isSuccess) {
    createdBy = getCompleteName(apiKeyRead.creator);
    email = apiKeyRead.creator.email;
  } else if (!isLoading) {
    createdBy = noPermission;
    email = noPermission;
  }

  const INITIAL_VALUES = {
    apiKeyName: user.givenName,
    apiKeyId: user.id,
    createdBy,
    email,
  };

  return (
    <RanaForm initialValues={INITIAL_VALUES}>
      <Flex direction="column">
        <RanaForm.Input
          isReadOnly
          field="apiKeyName"
          label={intl.formatMessage({
            id: "profile.api_key_name",
            defaultMessage: "API Key Name",
          })}
        />

        <RanaForm.Input
          isReadOnly
          field="apiKeyId"
          label={intl.formatMessage({
            id: "profile.api_key_id",
            defaultMessage: "API Key ID",
          })}
        />

        <RanaForm.Input
          isReadOnly
          as={isLoading ? Skeleton : undefined}
          field="createdBy"
          label={intl.formatMessage({
            id: "profile.created_by",
            defaultMessage: "Created By",
          })}
        />

        <RanaForm.Input
          isReadOnly
          as={isLoading ? Skeleton : undefined}
          field="email"
          type="email"
          label={intl.formatMessage({
            id: "profile.email",
            defaultMessage: "Email",
          })}
        />
      </Flex>
    </RanaForm>
  );
}

export function ProfileModalFormRegularUser({ user }: { user: AnyUser }) {
  const intl = useIntl();

  const userHasRole = "role" in user;
  const { data: userDetail, isLoading } = useUserQuery(userHasRole ? undefined : user.id);
  const role = userHasRole ? user.role : userDetail?.role;

  const roleDescription = (
    <Box>
      {Object.values(TenantRole).map((role) => (
        <Box key={role}>
          <Text as="strong">{userRoleString(intl, role)}:</Text>
          <Text mb={4}>{userRoleDescriptionString(intl, role)}</Text>
        </Box>
      ))}
    </Box>
  );
  const INITIAL_VALUES = {
    firstName: user.givenName,
    lastName: user.familyName,
    email: user.email,
    role: capitalizeFirst(role ?? undefined) ?? null,
  };

  return (
    <RanaForm initialValues={INITIAL_VALUES}>
      <Flex direction="column">
        <RanaForm.Input
          isReadOnly
          field="firstName"
          label={intl.formatMessage({
            id: "profile.first_name",
            defaultMessage: "First Name",
          })}
        />

        <RanaForm.Input
          isReadOnly
          field="lastName"
          label={intl.formatMessage({
            id: "profile.last_name",
            defaultMessage: "Last Name",
          })}
        />

        <RanaForm.Input
          isReadOnly
          field="email"
          type="email"
          label={intl.formatMessage({
            id: "profile.email",
            defaultMessage: "Email",
          })}
        />

        <RanaForm.Input
          isReadOnly
          field="role"
          label={intl.formatMessage({
            id: "profile.role",
            defaultMessage: "Organisation role",
          })}
          as={isLoading ? Skeleton : undefined}
          extraInfo={roleDescription}
        />
      </Flex>
    </RanaForm>
  );
}
